import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStandard, ButtonStandardHoverEffectType, ButtonStandardType } from '../../button/ButtonStandard/ButtonStandard';
import { CardBody } from '../../card/CardBody/CardBody';
import { CardHeader, HorizontalAlignment } from '../../card/CardHeader/CardHeader';
import { CardStandard, CardStandardProps } from '../../card/CardStandard/CardStandard';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useNotificationManager } from '../../hooks/useNotificationManager';
import { TextInput, TextInputType } from '../../input/TextInput/TextInput';
import { LoadingOverlay } from '../../misc/LoadingOverlay/LoadingOverlay';
import { MessageBox, MessageBoxType } from '../../misc/MessageBox/MessageBox';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import { FormGroup } from '../FormGroup/FormGroup';
import styles from './ForgotPasswordForm.module.css';
export type ForgotPasswordFormProps = PropsWithoutChildren<CardStandardProps> & {
  sendPasswortResetMail: (email: string) => Promise<void>;
  onCancel: () => void;
  defaultEmail?: string;
};
export const ForgotPasswordForm: FunctionComponent<ForgotPasswordFormProps> = React.memo(props => {
  const $ = _c(46);
  const {
    className,
    defaultEmail,
    sendPasswortResetMail,
    onCancel,
    ...remainingProps
  } = prepareProps({
    props,
    ignoreChildren: true
  });
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [sendingEmailFailed, setSendingEmailFailed] = useState(false);
  const [noEmailAddressEntered, setNoEmailAddressEntered] = useState(false);
  const {
    t
  } = useTranslation("record-it-components");
  const notificationManager = useNotificationManager();
  const handleSendEmailButtonClick = useEventCallback(() => {
    if (isSendingEmail) {
      return;
    }
    const sanitizedEmail = email.trim();
    if (sanitizedEmail === "") {
      setNoEmailAddressEntered(true);
      return;
    }
    setNoEmailAddressEntered(false);
    setIsSendingEmail(true);
    setSendingEmailFailed(false);
    sendPasswortResetMail(email).then(() => {
      notificationManager.success({
        title: t("form.ForgotPasswordForm.successNotification.title"),
        message: t("form.ForgotPasswordForm.successNotification.message")
      });
    }).catch(e => {
      console.error(e);
      setSendingEmailFailed(true);
    }).finally(() => {
      setIsSendingEmail(false);
    });
  });
  const T0 = CardStandard;
  const t0 = classes(className, styles.card);
  let t1;
  if ($[0] !== t) {
    t1 = t("form.ForgotPasswordForm.title");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <CardHeader textAlignment={HorizontalAlignment.Center}>{t1}</CardHeader>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== sendingEmailFailed || $[5] !== t) {
    t3 = sendingEmailFailed ? <MessageBox type={MessageBoxType.Error} className={styles.sendingEmailErrorMessageBox} data-testid="ForgotPasswordForm.SendingEmailFailedText">{t("form.ForgotPasswordForm.sendingEmailFailedText")}</MessageBox> : null;
    $[4] = sendingEmailFailed;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== noEmailAddressEntered || $[8] !== t) {
    t4 = noEmailAddressEntered ? <MessageBox type={MessageBoxType.Error} className={styles.sendingEmailErrorMessageBox} data-testid="ForgotPasswordForm.NoEmailAddressEnteredText">{t("form.ForgotPasswordForm.noEmailAddressEnteredText")}</MessageBox> : null;
    $[7] = noEmailAddressEntered;
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t) {
    t5 = t("form.ForgotPasswordForm.infoText");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <MessageBox type={MessageBoxType.Info} className={styles.infoMessageBox}>{t5}</MessageBox>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("form.ForgotPasswordForm.emailLabel");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== email || $[17] !== setEmail || $[18] !== t7) {
    t8 = <FormGroup><TextInput label={t7} type={TextInputType.Email} value={email} onInput={setEmail} data-testid="ForgotPasswordForm.EmailTextInput" /></FormGroup>;
    $[16] = email;
    $[17] = setEmail;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t) {
    t9 = t("form.ForgotPasswordForm.sendEmail");
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== handleSendEmailButtonClick || $[23] !== t9) {
    t10 = <div className={styles.sendEmailButtonWrapper}><ButtonStandard type={ButtonStandardType.Primary} onClick={handleSendEmailButtonClick} data-testid="ForgotPasswordForm.SendEmailButton">{t9}</ButtonStandard></div>;
    $[22] = handleSendEmailButtonClick;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t) {
    t11 = t("form.ForgotPasswordForm.cancel");
    $[25] = t;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== onCancel || $[28] !== t11) {
    t12 = <div className={styles.cancelButtonWrapper}><ButtonStandard type={ButtonStandardType.TextSecondary} hoverEffectType={ButtonStandardHoverEffectType.HalfTransparent} onClick={onCancel} data-testid="ForgotPasswordForm.CancelButton">{t11}</ButtonStandard></div>;
    $[27] = onCancel;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== isSendingEmail) {
    t13 = <LoadingOverlay active={isSendingEmail} data-testid="ForgotPasswordForm.LoadingOverlay" />;
    $[30] = isSendingEmail;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t10 || $[33] !== t12 || $[34] !== t13 || $[35] !== t3 || $[36] !== t4 || $[37] !== t6 || $[38] !== t8) {
    t14 = <CardBody>{t3}{t4}{t6}{t8}{t10}{t12}{t13}</CardBody>;
    $[32] = t10;
    $[33] = t12;
    $[34] = t13;
    $[35] = t3;
    $[36] = t4;
    $[37] = t6;
    $[38] = t8;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== T0 || $[41] !== remainingProps || $[42] !== t0 || $[43] !== t14 || $[44] !== t2) {
    t15 = <T0 {...remainingProps} className={t0}>{t2}{t14}</T0>;
    $[40] = T0;
    $[41] = remainingProps;
    $[42] = t0;
    $[43] = t14;
    $[44] = t2;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  return t15;
});